import Button from "components/atoms/Button";
import InputDate from "components/atoms/InputDate";
import InputSelect from "components/atoms/InputSelect";
import TableDataComponent from "components/organisms/TableData";
import moment from "moment";
import React from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { MonthOptions, PageMode } from "utils/constant";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import ActionButton from "components/atoms/ActionButton";
import {
  errorToastNotif,
  monthValueConverter,
  setDataToLocalStorage,
  successToastNotif,
} from "utils/common";
import { confirmAlert } from "react-confirm-alert";
import { useEffect } from "react";
import UjiSilangService from "services/UjiSilang";

const ujiSilangService = new UjiSilangService();

const Table = ({ changeMode }) => {
  const [filterState, setFilterState] = useState({
    filter_year: "",
    filter_month: "",
  });
  const [dataState, setDataState] = useState({
    page: 1,
    limit: 10,
    data: [],
    totalData: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  const { currentUser } = useSelector((state) => state.authReducer);

  const fetchInputData = async () => {
    setIsLoading(true);
    try {
      const response = await ujiSilangService.getAll({
        page: dataState.page,
        limit: dataState.limit,
        searchBy: `district_id,year,month`,
        searchValue: `${currentUser?.district_id},${filterState.filter_year},${filterState.filter_month}`,
      });

      setDataState((prevState) => ({
        ...prevState,
        data: response.uji_silang,
        limit: response.limit,
        currentPage: response.currentPage,
        totalData: response.total,
        nextPage: response.nextPage,
        previousPage: response.previousPage,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInputData();
  }, [dataState.page]);

  const onPageChange = (page) => {
    setDataState((prevState) => ({
      ...prevState,
      page: page,
    }));
  };

  const onRemoveData = (id) => {
    confirmAlert({
      title: "Apakah anda yakin ingin menghapus data?",
      message: "Data akan dihapus secara permanen",
      buttons: [
        {
          label: "Ya",
          onClick: async () => {
            await ujiSilangService
              .remove(id)
              .then((res) => {
                fetchInputData();
                successToastNotif("Data berhasil dihapus");
              })
              .catch((err) => {
                console.log(err);
                errorToastNotif(err);
              });
          },
        },
        {
          label: "Tidak",
        },
      ],
    });
  };

  const onEditData = (item) => {
    setDataToLocalStorage("SELECTED_ITEM", JSON.stringify(item));
    changeMode(PageMode.edit);
  };

  const columns = [
    {
      header: "Aksi",
      accessorKey: "action",
      cell: ({ row }) => (
        <div className="button-actions">
          <ActionButton
            onClick={() => onEditData(row.original)}
            btnType={"edit"}
          />
          <ActionButton
            onClick={() => onRemoveData(row.original.id)}
            btnType={"delete"}
          />
        </div>
      ),
    },
    {
      header: "Tanggal diinput fasyankes",
      accessorKey: "createdAt",
      cell: ({ row }) => moment(row.original.createdAt).format("YYYY-MM-DD"),
    },
    {
      header: "Provinsi",
      accessorKey: "province_id",
      cell: ({ row }) =>
        row.original.province_id ? row.original.province_id?.split("|")[1] : "",
      size: 250,
    },
    {
      header: "Kabupaten",
      accessorKey: "district_id",
      cell: ({ row }) =>
        row.original.district_id ? row.original.district_id?.split("|")[1] : "",
      size: 250,
    },
    {
      header: "Fasyankes",
      accessorKey: "fasyankes_id",
      cell: ({ row }) =>
        row.original.fasyankes_id
          ? row.original.fasyankes_id?.split("|")[1]
          : "",
      size: 260,
    },
    {
      header: "Tahun",
      accessorKey: "year",
      size: 100,
    },
    {
      header: "Bulan",
      accessorKey: "month",
      cell: ({ row }) =>
        row.original.month ? monthValueConverter(row.original.month) : "",
      size: 100,
    },
    {
      header: "Tanggal Sampel Dikirim",
      accessorKey: "tgl_sampel_dikirim",
      cell: ({ row }) => row.original.tgl_sampel_dikirim ?? "",
    },
    {
      header: "Jumlah (sedian darah=SD) (diinput fasyankes)",
      accessorKey: "jumsdfaskes",
      size: 200,
    },
    {
      header: "Nilai pembuatan SD tipis",
      accessorKey: "nilai_pmbt_sd_tpis",
      size: 160,
    },
    {
      header: "Nilai pembuatan SD tebal tidak terfiksasi",
      accessorKey: "nilai_pmbt_sd_tbal",
      size: 200,
    },
    {
      header: "Kualitas pewarnaan SD",
      accessorKey: "kualitas_pwrnaan_sd",
      size: 160,
    },
    {
      header: "Negatif benar",
      accessorKey: "jumsdneg_benar",
      size: 160,
    },
    {
      header: "Negatif palsu",
      accessorKey: "jumsdneg_palsu",
      size: 160,
    },
    {
      header: "Positif benar",
      accessorKey: "jumsdpos_benar",
      size: 160,
    },
    {
      header: "Jumlah sedian darah positif (mix spesies benar)",
      accessorKey: "jumpos_mix_benar",
      size: 210,
    },
    {
      header: "Jenis parasit penar",
      accessorKey: "parasyte_benar",
      cell: ({ row }) =>
        row.original?.parasyte_benar
          ? row.original?.parasyte_benar?.join(", ")
          : "",
      size: 160,
    },
    {
      header: "Jumlah sedian darah positif (pf spesies benar)",
      accessorKey: "jumpos_pf_benar",
      size: 210,
    },
    {
      header: "Jumlah sedian darah positif (pv spesies benar)",
      accessorKey: "jumpos_pv_benar",
      size: 210,
    },
    {
      header: "Jumlah sedian darah positif (po spesies benar)",
      accessorKey: "jumpos_po_benar",
      size: 210,
    },
    {
      header: "Jumlah sedian darah positif (pm spesies benar)",
      accessorKey: "jumpos_pm_benar",
      size: 210,
    },
    {
      header: "Total sedian darah positip (spesies benar)",
      accessorKey: "totpos_benar",
      size: 210,
    },
    {
      header: "Positif Palsu",
      accessorKey: "jumsdpos_palsu",
      size: 160,
    },
    {
      header: "Jumlah sedian darah positif (mix spesies palsu)",
      accessorKey: "jumpos_mix_palsu",
      size: 210,
    },
    {
      header: "Jenis parasit palsu",
      accessorKey: "parasyte_palsu",
      cell: ({ row }) =>
        row.original?.parasyte_palsu
          ? row.original?.parasyte_palsu?.join(", ")
          : "",
      size: 160,
    },
    {
      header: "Jumlah sedian darah positif (pf spesies palsu)",
      accessorKey: "jumpos_pf_palsu",
      size: 210,
    },
    {
      header: "Jumlah sedian darah positif (pv spesies palsu)",
      accessorKey: "jumpos_pv_palsu",
      size: 210,
    },
    {
      header: "Jumlah sedian darah positif (po spesies palsu)",
      accessorKey: "jumpos_po_palsu",
      size: 210,
    },
    {
      header: "Jumlah sedian darah positif (pm spesies palsu)",
      accessorKey: "jumpos_pm_palsu",
      size: 210,
    },
    {
      header: "Total sedian darah positip (spesies palsu)",
      accessorKey: "totpos_palsu",
      size: 210,
    },
  ];

  const tableInstance = useReactTable({
    columns,
    data: dataState?.data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <h1 className="page-title">Uji Silang Kab/Kota</h1>
      {/* Filter section start */}
      <div className="card-filter">
        <div className="filter-item">
          <InputDate
            placeholder={"Pilih tahun"}
            name={"filter_year"}
            dateFormat={"YYYY"}
            onChange={(e) => {
              const selectedYear = moment(e).format("YYYY");
              setFilterState({
                ...filterState,
                filter_year: selectedYear,
              });
            }}
            value={filterState.filter_year}
          />
        </div>
        <div className="filter-item">
          <InputSelect
            placeholder={"Pilih bulan"}
            data={MonthOptions}
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              setFilterState({
                ...filterState,
                filter_month: selectedValue,
              });
            }}
            value={MonthOptions.find(
              (item) => item.value === filterState.filter_month
            )}
          />
        </div>
        <Button
          variant={"primary"}
          size={"normal"}
          onClick={() => fetchInputData()}
        >
          Terapkan
        </Button>
      </div>
      {/* Filter section end */}

      <TableDataComponent
        columnLength={columns?.length}
        tableInstance={tableInstance}
        onPageChange={onPageChange}
        loading={isLoading}
        currentPage={dataState?.currentPage}
        limit={dataState?.limit}
        nextPage={dataState?.nextPage}
        previousPage={dataState?.previousPage}
        total={dataState?.totalData}
      />
    </div>
  );
};

export default Table;
